import { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Filter, Search } from "lucide-react";
import DateRangeModal from "../../components/Filter/DateRangeModal";
import { getDateRange } from "../utils/dateUtils/filter_date";
import "../../components/Filter/index.css";

const PERIODS = [
    ["today", "Hoje"],
    ["yesterday", "Ontem"],
    ["7days", "Semana"],
    ["thisMonth", "Mês"],
];

/** Qual botão de período está ativo, a partir das datas aplicadas. */
export function activePeriod(start, end) {
    if (!start && !end) return "all";
    const hit = PERIODS.find(([id]) => {
        const r = getDateRange(id);
        return r.startDate === start && r.endDate === end;
    });
    return hit ? hit[0] : "custom";
}

const ddmm = (s) => {
    const [, m, d] = (s || "").split("-");
    return d && m ? `${d}/${m}` : "…";
};

function Dropdown({ label, options, value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const current = options.find((o) => o.value === value) || options[0];
    return (
        <div className="adm-filter-dropdown" ref={ref}>
            <button
                type="button"
                className={`filter-dropdown-trigger ${open ? "open" : ""}`}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={label}
                onClick={() => setOpen((v) => !v)}
            >
                <span>{current?.label}</span>
                <ChevronDown size={14} />
            </button>
            {open && (
                <div className="filter-dropdown-container" role="listbox" aria-label={label}>
                    {options.map((o) => (
                        <div
                            key={o.value}
                            role="option"
                            aria-selected={o.value === value}
                            className={`filter-dropdown-item ${o.value === value ? "selected" : ""}`}
                            onClick={() => { onChange(o.value); setOpen(false); }}
                        >
                            {o.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Filtro das telas do admin com o visual do filtro de Vendas (FiltersSection):
 * período em pílulas (aplica na hora; "Personalizado" abre o calendário) e,
 * abaixo, buscas + dropdowns que valem ao clicar em Filtrar (ou Enter).
 *
 * `values` são os filtros aplicados (vêm da URL); `onApply(next)` recebe todos.
 * `children` entram na linha (ex.: chip de filtro exato vindo de outra seção).
 */
export default function AdminFilters({ values, onApply, period = false, searches = [], selects = [], children }) {
    const [draft, setDraft] = useState(values);
    const [dateModal, setDateModal] = useState(false);
    const valuesKey = JSON.stringify(values);

    // A URL é a fonte da verdade: se ela mudar (link, voltar, chip), os campos acompanham.
    useEffect(() => {
        setDraft(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valuesKey]);

    const submit = (extra = {}) => onApply({ ...values, ...draft, ...extra });
    const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    const current = period ? activePeriod(values.start_date, values.end_date) : null;
    const pickPeriod = (id) => {
        if (id === "custom") { setDateModal(true); return; }
        const r = id === "all" ? { startDate: "", endDate: "" } : getDateRange(id);
        submit({ start_date: r.startDate, end_date: r.endDate });
    };

    return (
        <>
            {period && (
                <>
                    <div className="filters-date">
                        <div className="flex flex-wrap justify-center md:justify-end gap-1.5 md:gap-2">
                            {PERIODS.map(([id, label]) => (
                                <button key={id} type="button" className={`period-btn ${current === id ? "active" : ""}`}
                                    onClick={() => pickPeriod(id)}>
                                    {label}
                                </button>
                            ))}
                            <button type="button" className={`period-btn ${current === "all" ? "active" : ""}`}
                                onClick={() => pickPeriod("all")}>
                                Tudo
                            </button>
                            <button type="button"
                                className={`period-btn period-btn-custom adm-period-custom ${current === "custom" ? "active" : ""}`}
                                onClick={() => pickPeriod("custom")}>
                                <CalendarDays size={13} />
                                {current === "custom"
                                    ? (values.start_date === values.end_date
                                        ? ddmm(values.start_date)
                                        : `${ddmm(values.start_date)} - ${ddmm(values.end_date)}`)
                                    : "Personalizado"}
                            </button>
                        </div>
                    </div>
                    <DateRangeModal
                        open={dateModal}
                        initialStart={values.start_date}
                        initialEnd={values.end_date}
                        onApply={(r) => { setDateModal(false); submit({ start_date: r.startDate, end_date: r.endDate }); }}
                        onClose={() => setDateModal(false)}
                    />
                </>
            )}

            {(searches.length > 0 || selects.length > 0 || children) && (
            <div className="filters-section adm-filters">
                {searches.map((s) => (
                    <div key={s.key} className="adm-filter-search">
                        <Search size={15} aria-hidden="true" />
                        <input
                            type="text"
                            className="filter-input"
                            placeholder={s.placeholder}
                            value={draft[s.key] || ""}
                            onChange={(e) => setField(s.key, e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                        />
                    </div>
                ))}
                {selects.map((s) => (
                    <Dropdown key={s.key} label={s.label} options={s.options}
                        value={draft[s.key] || ""} onChange={(v) => setField(s.key, v)} />
                ))}
                {children}
                <button type="button" className="btn-primary-filter adm-filter-submit" onClick={() => submit()}>
                    <Filter size={15} />
                    Filtrar
                </button>
            </div>
            )}
        </>
    );
}
